import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, PublicEstimateDto } from '../services/api';
import { useBranding } from '../context/BrandingContext';
import { useAuth } from '../context/AuthContext';
import { C } from '../components/UI';
import { SignaturePad } from '../components/SignaturePad';
import { PaymentForm } from '../components/PaymentForm';

// Public, tokenized estimate view — the one page in this app reachable with no login. A
// prospect gets a link by email (see EstimateBuilderService.SendAsync) and lands here directly.
// Built the same shape as PreorderPage.tsx (public route, outside RoleGuard, BrandingContext
// for tenant chrome) — no new page pattern, just a new use of an existing one.

export default function PublicEstimatePage() {
    const { token } = useParams<{ token: string }>();
    const branding = useBranding();
    const { user } = useAuth();
    const [estimate, setEstimate] = useState<PublicEstimateDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [mode, setMode] = useState<'view' | 'signing' | 'paying'>('view');
    const [signature, setSignature] = useState<string | null>(null);
    const [signedByName, setSignedByName] = useState('');
    const [busy, setBusy] = useState(false);
    const [togglingId, setTogglingId] = useState<string | null>(null);
    const [payClientSecret, setPayClientSecret] = useState<string | null>(null);
    const [payAmount, setPayAmount] = useState(0);
    // true when paying for an estimate already approved in person (Runner-witnessed signing) —
    // just needs a card, not another approve() call. False mid-signing here, where payment is
    // the last step before the approve() call itself fires.
    const [payingStandalone, setPayingStandalone] = useState(false);

    useEffect(() => {
        if (!token) return;
        api.getPublicEstimate(token).then(setEstimate).catch(e => setError(e.message)).finally(() => setLoading(false));
    }, [token]);

    const getGeo = (): Promise<{ lat?: number; lng?: number }> =>
        new Promise(resolve => {
            if (!navigator.geolocation) { resolve({}); return; }
            navigator.geolocation.getCurrentPosition(
                pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                () => resolve({}),
                { timeout: 4000 }
            );
        });

    const approve = async () => {
        if (!token) return;
        setBusy(true); setError('');
        try {
            const geo = await getGeo();
            const updated = await api.approvePublicEstimate(token, {
                signatureBase64Png: signature,
                lat: geo.lat, lng: geo.lng,
                signedByName: signedByName.trim() || undefined,
            });
            setEstimate(updated);
            setMode('view');
        } catch (e: any) { setError(e.message); }
        finally { setBusy(false); }
    };

    const decline = async () => {
        if (!token) return;
        setBusy(true); setError('');
        try { setEstimate(await api.declinePublicEstimate(token)); }
        catch (e: any) { setError(e.message); }
        finally { setBusy(false); }
    };

    // Card authorization (not a charge) ahead of approval — same manual-capture pattern used to
    // book a normal job, just against the estimate's total instead of a job.
    const startPayment = async (standalone: boolean) => {
        if (!token) return;
        if (!standalone) {
            if (!signature) { setError('Have the customer sign above first.'); return; }
            if (!signedByName.trim()) { setError("Enter the customer's name."); return; }
        }
        setBusy(true); setError('');
        try {
            const { clientSecret, amount } = await api.createPublicEstimatePaymentIntent(token);
            setPayClientSecret(clientSecret);
            setPayAmount(amount);
            setPayingStandalone(standalone);
            setMode('paying');
        } catch (e: any) { setError(e.message); }
        finally { setBusy(false); }
    };

    const onPaymentSuccess = async () => {
        if (!token) return;
        setBusy(true); setError('');
        try {
            const updated = await api.confirmPublicEstimatePayment(token);
            if (payingStandalone) { setEstimate(updated); setMode('view'); }
            else { await approve(); }
        } catch (e: any) { setError(e.message); }
        finally { setBusy(false); }
    };

    const toggleLineItem = async (lineItemId: string, selected: boolean) => {
        if (!token) return;
        setTogglingId(lineItemId); setError('');
        try { setEstimate(await api.setPublicEstimateLineItemSelected(token, lineItemId, selected)); }
        catch (e: any) { setError(e.message); }
        finally { setTogglingId(null); }
    };

    const downloadPdf = async () => {
        if (!token) return;
        try {
            const { pdfUrl } = await api.getPublicEstimatePdf(token);
            window.open(pdfUrl, '_blank');
        } catch (e: any) { setError(e.message); }
    };

    const navText = branding.navTextColor ?? '#ffffff';
    const COMPANY = branding.appName || 'ServiceTrek';

    if (loading) {
        return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textSub }}>Loading estimate…</div>;
    }

    if (!estimate) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8, padding: 24, textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700 }}>This link isn't valid</div>
                <div style={{ color: C.textSub, fontSize: 14 }}>{error || 'It may have expired or already been used. Contact the business that sent it to you for a new link.'}</div>
            </div>
        );
    }

    const canRespond = estimate.status === 'Sent' || estimate.status === 'Viewed';
    // Declined is the one truly-done state where there's nothing left to manage from an account.
    const showAccountBanner = estimate.status !== 'Declined';

    return (
        <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'Inter', sans-serif", color: C.text }}>
            <div style={{ background: branding.primaryColor, padding: '0 24px', display: 'flex', alignItems: 'center', gap: 16, height: 56 }}>
                {branding.logoUrl
                    ? <img src={branding.logoUrl} alt={branding.appName} style={{ height: 32, maxWidth: 140, objectFit: 'contain' }} />
                    : <span style={{ fontSize: 18, fontWeight: 700, color: navText, letterSpacing: -0.5 }}>{COMPANY}</span>}
                <span style={{ color: navText, fontSize: 13, opacity: 0.7 }}>· Estimate #EB-{estimate.number}</span>
            </div>

            <div style={{ maxWidth: 640, margin: '0 auto', padding: '32px 20px 80px' }}>
                {/* Account banner — a prospect who's already a customer (or becomes one) should be
                    able to manage this estimate from their account instead of only this one-time
                    link. Not shown once resolved (Approved/Declined) — nothing left to manage. */}
                {showAccountBanner && (
                    user ? (
                        user.role === 'Customer' && (
                            <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                <span>Signed in as <strong>{user.name}</strong></span>
                                <Link to={`/customer?estimateId=${estimate.id}`} style={{ color: branding.primaryColor, fontWeight: 600, textDecoration: 'none' }}>
                                    View in My Estimates →
                                </Link>
                            </div>
                        )
                    ) : (
                        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <span style={{ color: C.textSub }}>Have an account, or want to save this for later?</span>
                            <Link to={`/login?redirect=${encodeURIComponent(`/estimate/${token}`)}`} style={{ color: branding.primaryColor, fontWeight: 600, textDecoration: 'none' }}>
                                Log in or create an account →
                            </Link>
                        </div>
                    )
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                    <div>
                        <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 4px' }}>Estimate for {estimate.prospectName}</h1>
                        <div style={{ color: C.textSub, fontSize: 13 }}>{estimate.jobSiteAddress}</div>
                    </div>
                    <button onClick={downloadPdf} style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer', color: C.text }}>
                        📄 Download PDF
                    </button>
                </div>

                {estimate.status === 'Approved' && (
                    <div style={{ background: C.greenBg, color: C.green, padding: '12px 16px', borderRadius: 8, marginBottom: 20, fontSize: 14, fontWeight: 600 }}>
                        ✓ You approved this estimate{estimate.signedAt ? ` on ${new Date(estimate.signedAt).toLocaleDateString()}` : ''}.
                        {!estimate.paymentAuthorized && ' One more step below — add a payment method to get scheduled.'}
                    </div>
                )}
                {estimate.status === 'Declined' && (
                    <div style={{ background: '#FDECEA', color: '#9B1C1C', padding: '12px 16px', borderRadius: 8, marginBottom: 20, fontSize: 14, fontWeight: 600 }}>
                        You declined this estimate.
                    </div>
                )}
                {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 16 }}>{error}</div>}

                <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.border}`, padding: 20, marginBottom: 20 }}>
                    <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 14px' }}>Line items</h2>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {(() => {
                            const renderedTierGroups = new Set<string>();
                            return estimate.lineItems.map(li => {
                                if (li.tierGroup) {
                                    if (renderedTierGroups.has(li.tierGroup)) return null;
                                    renderedTierGroups.add(li.tierGroup);
                                    const options = estimate.lineItems.filter(x => x.tierGroup === li.tierGroup);
                                    return (
                                        <div key={li.tierGroup} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 12 }}>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', marginBottom: 8 }}>
                                                Choose one — {li.tierGroup}
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                {options.map(opt => (
                                                    <label key={opt.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontSize: 14, cursor: canRespond ? 'pointer' : 'default', gap: 10 }}>
                                                        <span style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                                            <input type="radio" name={`tier-${li.tierGroup}`} checked={opt.isSelected}
                                                                disabled={!canRespond || togglingId === opt.id}
                                                                onChange={() => toggleLineItem(opt.id, true)}
                                                                style={{ marginTop: 3 }} />
                                                            <span>
                                                                <strong>{opt.description}</strong>
                                                                <div style={{ color: C.textSub, fontSize: 12 }}>{opt.quantity} {opt.unit} × ${opt.unitPrice.toFixed(2)}</div>
                                                                {opt.photoUrl && <img src={opt.photoUrl} alt="" style={{ marginTop: 6, maxWidth: 200, maxHeight: 140, borderRadius: 8, display: 'block' }} />}
                                                            </span>
                                                        </span>
                                                        <strong>${opt.lineTotal.toFixed(2)}</strong>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                }
                                return (
                                    <div key={li.id}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, opacity: li.isOptional && !li.isSelected ? 0.55 : 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                                {li.isOptional && (
                                                    <input type="checkbox" checked={li.isSelected}
                                                        disabled={!canRespond || togglingId === li.id}
                                                        onChange={() => toggleLineItem(li.id, !li.isSelected)}
                                                        style={{ marginTop: 3 }} />
                                                )}
                                                <div>
                                                    <span style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', marginRight: 6 }}>{li.lineType}</span>
                                                    <strong>{li.description}</strong>
                                                    {li.isOptional && <span style={{ marginLeft: 6, fontSize: 11, color: '#7C3AED', fontWeight: 600 }}>Optional</span>}
                                                    <div style={{ color: C.textSub, fontSize: 12 }}>{li.quantity} {li.unit} × ${li.unitPrice.toFixed(2)}</div>
                                                </div>
                                            </div>
                                            <strong>{li.isOptional && !li.isSelected ? '—' : `$${li.lineTotal.toFixed(2)}`}</strong>
                                        </div>
                                        {li.photoUrl && <img src={li.photoUrl} alt="" style={{ marginTop: 6, maxWidth: '100%', maxHeight: 200, borderRadius: 8, display: 'block' }} />}
                                    </div>
                                );
                            });
                        })()}
                    </div>

                    <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 16, paddingTop: 12 }}>
                        <TotalRow label="Subtotal" value={estimate.subtotal} />
                        {estimate.discountAmount > 0 && <TotalRow label={estimate.discountLabel || 'Discount'} value={-estimate.discountAmount} />}
                        {estimate.taxAmount > 0 && <TotalRow label="Tax" value={estimate.taxAmount} />}
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 700, marginTop: 6 }}>
                            <span>Total</span><span>${estimate.totalAmount.toFixed(2)}</span>
                        </div>
                        {estimate.depositAmount != null && (
                            <div style={{ fontSize: 13, color: C.textSub, marginTop: 4 }}>Deposit required: ${estimate.depositAmount.toFixed(2)}</div>
                        )}
                    </div>
                </div>

                {estimate.paymentTerms && (
                    <InfoBlock title="Payment terms" text={estimate.paymentTerms} />
                )}
                {estimate.warrantyNotes && (
                    <InfoBlock title="Warranty" text={estimate.warrantyNotes} />
                )}

                {canRespond && mode === 'view' && (
                    <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
                        <button onClick={decline} disabled={busy}
                            style={{ flex: 1, padding: '12px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', fontSize: 14, cursor: 'pointer', color: C.text }}>
                            Decline
                        </button>
                        <button onClick={() => setMode('signing')} disabled={busy}
                            style={{ flex: 2, padding: '12px', borderRadius: 8, border: 'none', background: branding.primaryColor, color: navText, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                            Approve estimate
                        </button>
                    </div>
                )}

                {estimate.status === 'Approved' && !estimate.paymentAuthorized && mode === 'view' && (
                    <button onClick={() => startPayment(true)} disabled={busy}
                        style={{ width: '100%', padding: '12px', borderRadius: 8, border: 'none', background: branding.primaryColor, color: navText, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 24 }}>
                        {busy ? 'Loading…' : 'Add payment method'}
                    </button>
                )}

                {canRespond && mode === 'signing' && (
                    <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.border}`, padding: 20, marginTop: 20 }}>
                        <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 12px' }}>Approve &amp; sign</h2>
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ display: 'block', fontSize: 12, color: C.textSub, marginBottom: 5 }}>Your name</label>
                            <input value={signedByName} onChange={e => setSignedByName(e.target.value)} placeholder="Type your full name"
                                style={{ width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                        </div>
                        <SignaturePad onChange={setSignature} />
                        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                            <button onClick={() => setMode('view')} disabled={busy}
                                style={{ flex: 1, padding: '12px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', fontSize: 14, cursor: 'pointer' }}>
                                Back
                            </button>
                            <button onClick={() => startPayment(false)} disabled={busy}
                                style={{ flex: 2, padding: '12px', borderRadius: 8, border: 'none', background: branding.primaryColor, color: navText, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                                {busy ? 'Loading…' : 'Continue to payment'}
                            </button>
                        </div>
                    </div>
                )}

                {mode === 'paying' && payClientSecret && (
                    <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.border}`, padding: 20, marginTop: 20 }}>
                        <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 4px' }}>Add a payment method</h2>
                        <p style={{ fontSize: 13, color: C.textSub, margin: '0 0 14px' }}>Your card is authorized now, not charged — you're only billed once the job is complete.</p>
                        <PaymentForm
                            jobId={estimate.id}
                            amount={payAmount}
                            clientSecret={payClientSecret}
                            onSuccess={onPaymentSuccess}
                            onCancel={() => setMode(payingStandalone ? 'view' : 'signing')}
                        />
                    </div>
                )}

                {estimate.contractDisclaimerText && (
                    <p style={{ fontSize: 11, color: C.textMuted, marginTop: 28 }}>{estimate.contractDisclaimerText}</p>
                )}
            </div>
        </div>
    );
}

function TotalRow({ label, value }: { label: string; value: number }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.textSub, marginBottom: 2 }}>
            <span>{label}</span><span>{value < 0 ? '-' : ''}${Math.abs(value).toFixed(2)}</span>
        </div>
    );
}

function InfoBlock({ title, text }: { title: string; text: string }) {
    return (
        <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.border}`, padding: 16, marginBottom: 16 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 6px' }}>{title}</h3>
            <p style={{ fontSize: 13, color: C.textSub, margin: 0, whiteSpace: 'pre-wrap' }}>{text}</p>
        </div>
    );
}
